import { Modal, Box, Button, Typography } from "@mui/material";
import MDButton from "components/MDButton";
import { cloneDeep } from "lodash";
import { useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";

import TextDisplay from "../../atoms/TextDisplay";
import BooleanSetting from "../../atoms/BooleanSetting";
import InputSetting from "../../atoms/InputSetting";
import SingleSelectDropdown from "../../atoms/SingleSelectDropdown";
import MultiSelectDropdown from "../../atoms/MultiSelectDropdown";
import MultiSelectCheckbox from "../../atoms/MultiSelectCheckbox";

const componentTypeMap = {
  boolean: {
    Component: BooleanSetting,
  },
  number: {
    Component: InputSetting,
  },
  "single-select-dropdown": {
    Component: SingleSelectDropdown,
  },
  "multi-select-dropdown": {
    Component: MultiSelectDropdown,
  },
  "text-display": {
    Component: TextDisplay,
  },
  "multi-select-checkbox": {
    Component: MultiSelectCheckbox,
  },
};

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "80%",
  height: "80%",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  "& .MuiFormHelperText-root": { color: "#d32f2f" },
  borderRadius: "0.5rem",
  outline: "none",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  overflow: "auto",
  width: "35%",
};

function TestModal({ open, handleClose, modalData, setParentState, setting }) {
  const [canDuplicateRowColumn, setCanDuplicateRowColumn] = useState(false);
  const [duplicationBtnName, setDuplicationBtnName] = useState("");
  const [modalHasMoreThanOneGroup, setModalHasMoreThanOneGroup] = useState(false);
  const [currModalData, setCurrModalData] = useState(null);

  useEffect(() => {
    if (open && modalData) {
      setCurrModalData(cloneDeep(modalData));
    }
  }, [open, modalData]);

  const deleteGroup = (duplicationGroupNumber) => {
    const updatedModalData = currModalData.filter(
      (dataField) => dataField?.duplication?.duplicationGroup !== duplicationGroupNumber
    );

    setCurrModalData(updatedModalData);
  };

  useEffect(() => {
    const groupCount = [];
    if (currModalData?.length) {
      currModalData.forEach((field) => {
        if (field?.duplication?.canDuplicate) {
          setCanDuplicateRowColumn(true);
          setDuplicationBtnName(field?.duplication?.duplicationBtnName);
        }

        if (field?.duplication && !groupCount.includes(field?.duplication?.duplicationGroup)) {
          groupCount.push(field?.duplication?.duplicationGroup);
        }
      });

      if (groupCount.length > 1) {
        setModalHasMoreThanOneGroup(true);
      } else {
        setModalHasMoreThanOneGroup(false);
      }
    }
  }, [currModalData]);

  const closeModalWithoutClickingSave = () => {
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={closeModalWithoutClickingSave}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={{ ...modalStyle }}>
        <Box>
          {currModalData?.map?.((field, fieldIdx) => {
            const ComponentToRender = componentTypeMap[field.settings_data_type].Component;

            let options = field.options || [];

            if (field.optionsCalculatedFrom) {
              const fieldToCalculateFrom = field.optionsCalculatedFrom;

              const fieldToComputeOptionsFrom = currModalData.find(
                (fieldData) => fieldData.columnId === fieldToCalculateFrom
              );

              const computedOptions =
                setting.metadata.globalDropdownOptions[
                  fieldToComputeOptionsFrom?.field_value?.value
                ];
              options = computedOptions;
            }

            return (
              <Box sx={{ display: "flex" }}>
                <Typography sx={{ mr: 1, whiteSpace: "nowrap" }}>{field?.field_label}</Typography>
                <ComponentToRender
                  setting={{ ...field, options }}
                  passWholeOption
                  setParentState={(newChildValue) => {
                    const copyModalData = cloneDeep(currModalData);
                    copyModalData[fieldIdx].field_value = newChildValue;
                    // When users switch between "channel" and "user", clear the recipient field
                    if (newChildValue?.value === "channel" || newChildValue?.value === "user") {
                      copyModalData[fieldIdx + 1].field_value = [];
                    }

                    setCurrModalData(copyModalData);
                  }}
                />
                {field?.duplication?.showTrashIcon && modalHasMoreThanOneGroup && (
                  <DeleteIcon
                    onClick={() => {
                      deleteGroup(field.duplication.duplicationGroup);
                    }}
                  />
                )}
              </Box>
            );
          })}
          {canDuplicateRowColumn && (
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Button
                onClick={() => {
                  const copyModalData = cloneDeep(currModalData);
                  const highestDuplicationGroup = copyModalData.reduce((maxGroupNum, field) => {
                    if (field?.duplication && field?.duplication?.duplicationGroup > maxGroupNum) {
                      return field?.duplication?.duplicationGroup;
                    }
                    return maxGroupNum;
                  }, 0);

                  const groupToDuplicateFrom = copyModalData.filter(
                    (field) => field?.duplication?.duplicationGroup === highestDuplicationGroup
                  );
                  const cloneGroupToDuplicateFrom = cloneDeep(groupToDuplicateFrom);

                  cloneGroupToDuplicateFrom.forEach((field) => {
                    field.duplication.duplicationGroup += 1;
                    switch (field.columnId) {
                      case "orgAndAllTeams":
                        field.field_value = [
                          { value: "organization", display: "Entire Organization" },
                        ];
                        break;
                      case "channelOrUser":
                        field.field_value = { value: "channel", display: "Channel" };
                        break;
                      case "recipientList":
                        field.field_value = [];
                        break;
                      default:
                        field.field_value = null;
                        break;
                    }
                  });

                  copyModalData.push(...cloneGroupToDuplicateFrom);

                  setCurrModalData(copyModalData);
                }}
                variant="gradient"
                color="newColor"
              >
                {duplicationBtnName}
              </Button>
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <MDButton
            color="gray80"
            style={{ marginRight: "10px" }}
            onClick={closeModalWithoutClickingSave}
          >
            CANCEL
          </MDButton>
          <MDButton
            color="primaryRed"
            onClick={() => {
              setParentState(currModalData);
              handleClose();
            }}
          >
            SAVE
          </MDButton>
        </Box>
      </Box>
    </Modal>
  );
}

export default TestModal;
